import { isNullish } from 'remeda';
import {
  BaseConversationUrgency,
  getUrgencyDisplay,
  UrgencyDisplayProps,
} from 'src/graphql/entities/conversation-urgency';
import type { CustomViewEntity } from 'src/graphql/entities/custom-view';
import { ClientAttribute } from 'src/graphql/entities/custom-view/custom-view-config/client/constants';
import { ConversationAttribute } from 'src/graphql/entities/custom-view/custom-view-config/conversation/constants';
import { SiteAttribute } from 'src/graphql/entities/custom-view/custom-view-config/site/constants';
import {
  CustomViewAttribute,
  UIType,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { EntityNames } from 'src/graphql/types';
import { isStringBooleanTrue } from 'src/utils/strings';

export type CardGlyphDisplay = UrgencyDisplayProps;

interface CardGlyphRole {
  attribute: string;
  /** `null` keeps the glyph out of the card, without reserving space for it. */
  getDisplay: (rawValue: unknown) => CardGlyphDisplay | null;
}

export enum CardLayoutKind {
  ChipLine = 'chipLine',
  TwoColumn = 'twoColumn',
}

/** How one attribute draws as a chip; every value gets exactly one pill or one visual. */
export enum ChipVisual {
  Pill = 'pill',
  MonoPill = 'monoPill',
  Id = 'id',
  ServiceLevel = 'serviceLevel',
  Avatar = 'avatar',
  Boolean = 'boolean',
  Date = 'date',
  /** The renderer already draws its own bubble or chip. */
  Raw = 'raw',
}

export enum SlotVisual {
  Initials = 'initials',
  ServiceLevelDot = 'serviceLevelDot',
  Icon = 'icon',
  /** Only a true value qualifies. */
  Check = 'check',
}

export interface SlotFallbackRole {
  visual: SlotVisual;
  getIcon?: (rawValue: unknown) => string;
}

const COLUMN_ROLES = [
  'slot',
  'title',
  'subtitle',
  'time',
  'trailingTop',
  'trailingBottom',
] as const;

type CardColumnRole = (typeof COLUMN_ROLES)[number];

export interface CardLayoutRoles {
  kind: CardLayoutKind;
  /** Leading 20px slot, left of the first line. */
  slot?: string;
  /** First line; ellipsised when it does not fit. */
  title: string;
  /** Under the title, `TwoColumn` only. */
  subtitle?: string;
  /** Right of the first line. */
  time?: string;
  /** Top of the right-hand column, `TwoColumn` only. */
  trailingTop?: string;
  /** Under `trailingTop`, `TwoColumn` only. */
  trailingBottom?: string;
  /** Inline glyph before the title; before the first chip while the title is off. */
  glyph?: CardGlyphRole;
  /** Chip visual per attribute where the `uiType` default is not the right one. */
  chipVisuals?: Record<string, ChipVisual>;
  /** Attributes the slot may show while `slot` is off. */
  slotFallbacks?: Record<string, SlotFallbackRole>;
  /** Order in which the displayed `slotFallbacks` attributes are tried; the first one with a value wins. */
  slotFallbackPriority?: string[];
  /** Roles that keep their attribute on the card even when it is switched off in Display. */
  pinnedRoles?: CardColumnRole[];
  /** Attributes the card never shows, even when they are on in Display. */
  hiddenAttributes?: string[];
}

const INLINE_URGENCIES = new Set<string>([
  BaseConversationUrgency.Urgent,
  BaseConversationUrgency.LifeSafety,
]);

const PLATFORM_USER_REQUESTER_TYPE = 'platform_user';

const CONVERSATION_CARD_LAYOUT: CardLayoutRoles = {
  kind: CardLayoutKind.ChipLine,
  slot: ConversationAttribute.conversationStatus,
  title: ConversationAttribute.subject,
  time: ConversationAttribute.lastMessageReceivedAt,
  glyph: {
    attribute: ConversationAttribute.conversationUrgency,
    getDisplay: (urgency) =>
      INLINE_URGENCIES.has(String(urgency)) ? getUrgencyDisplay(String(urgency)) : null,
  },
  chipVisuals: {
    [ConversationAttribute.requesterEmail]: ChipVisual.MonoPill,
    [ConversationAttribute.requesterPhone]: ChipVisual.MonoPill,
    [ConversationAttribute.addressZip]: ChipVisual.MonoPill,
    [ConversationAttribute.serviceLevel]: ChipVisual.ServiceLevel,
  },
  slotFallbacks: {
    [ConversationAttribute.assigneeFullName]: { visual: SlotVisual.Initials },
    [ConversationAttribute.serviceLevel]: { visual: SlotVisual.ServiceLevelDot },
    [ConversationAttribute.requesterType]: {
      visual: SlotVisual.Icon,
      getIcon: (requesterType) =>
        requesterType === PLATFORM_USER_REQUESTER_TYPE ? 'user-cog' : 'user',
    },
    [ConversationAttribute.membershipRegistered]: { visual: SlotVisual.Check },
    [ConversationAttribute.clientTosAccepted]: { visual: SlotVisual.Check },
  },
  slotFallbackPriority: [
    ConversationAttribute.assigneeFullName,
    ConversationAttribute.serviceLevel,
    ConversationAttribute.requesterType,
    ConversationAttribute.membershipRegistered,
    ConversationAttribute.clientTosAccepted,
  ],
};

const SITE_CARD_LAYOUT: CardLayoutRoles = {
  kind: CardLayoutKind.TwoColumn,
  title: SiteAttribute.siteName,
  subtitle: SiteAttribute.siteAddress,
  pinnedRoles: ['subtitle'],
  trailingTop: SiteAttribute.clientActivatedMembership,
  trailingBottom: SiteAttribute.updatedAt,
  // the health donut is drawn for a table cell and has no chip form
  hiddenAttributes: [SiteAttribute.siteHealth],
};

// Clients carry no address; the email is the secondary line that is on by default.
const CLIENT_CARD_LAYOUT: CardLayoutRoles = {
  kind: CardLayoutKind.TwoColumn,
  title: ClientAttribute.name,
  subtitle: ClientAttribute.email,
  trailingTop: ClientAttribute.phone,
  trailingBottom: ClientAttribute.updatedAt,
};

/** An entity renders as cards on a phone only when it has a layout here; the rest keep the table. */
export const CARD_LAYOUT_BY_ENTITY = {
  [EntityNames.Conversation]: CONVERSATION_CARD_LAYOUT,
  [EntityNames.Site]: SITE_CARD_LAYOUT,
  [EntityNames.Client]: CLIENT_CARD_LAYOUT,
} satisfies Partial<Record<CustomViewEntity, CardLayoutRoles>>;

export type CardLayoutEntity = keyof typeof CARD_LAYOUT_BY_ENTITY;

export const isCardLayoutEntity = (
  entityName: CustomViewEntity,
): entityName is CardLayoutEntity => entityName in CARD_LAYOUT_BY_ENTITY;

export interface CardColumns extends Partial<Record<CardColumnRole, CustomViewAttribute>> {
  glyph?: CustomViewAttribute;
  chips: CustomViewAttribute[];
  /** A column listed here may also be a chip. */
  slotFallbackColumns: CustomViewAttribute[];
}

/**
 * `columns` is already in Display order, so the chips keep it; a column switched off in Display is
 * simply absent, except for a pinned role, which is resolved from `allAttributes`.
 */
export const splitCardColumns = (
  columns: CustomViewAttribute[],
  layout: CardLayoutRoles,
  allAttributes: CustomViewAttribute[] = [],
): CardColumns => {
  const cardColumns: CardColumns = { chips: [], slotFallbackColumns: [] };

  for (const column of columns) {
    if (layout.hiddenAttributes?.includes(column.attribute)) {
      continue;
    }

    if (layout.slotFallbacks?.[column.attribute]) {
      cardColumns.slotFallbackColumns.push(column);
    }

    const role = COLUMN_ROLES.find((candidate) => layout[candidate] === column.attribute);

    if (role) {
      cardColumns[role] = column;
    } else if (column.attribute === layout.glyph?.attribute) {
      cardColumns.glyph = column;
    } else {
      cardColumns.chips.push(column);
    }
  }

  for (const role of layout.pinnedRoles ?? []) {
    cardColumns[role] ??= allAttributes.find(({ attribute }) => attribute === layout[role]);
  }

  return cardColumns;
};

export const isEmptyAttributeValue = (rawValue: unknown, column: CustomViewAttribute) =>
  isNullish(rawValue) ||
  rawValue === '' ||
  (Array.isArray(rawValue) && rawValue.length === 0) ||
  (column.uiType === UIType.BubbleNumber && !rawValue) ||
  (typeof rawValue === 'string' && column.formatValue?.(rawValue) === '');

const CHIP_VISUAL_BY_UI_TYPE: Partial<Record<UIType, ChipVisual>> = {
  [UIType.Text]: ChipVisual.Pill,
  [UIType.List]: ChipVisual.Pill,
  [UIType.Textarea]: ChipVisual.Pill,
  [UIType.Number]: ChipVisual.Id,
  [UIType.Avatar]: ChipVisual.Avatar,
  [UIType.Boolean]: ChipVisual.Boolean,
  [UIType.Date]: ChipVisual.Date,
  [UIType.SinceTime]: ChipVisual.Date,
};

export const getChipVisual = (column: CustomViewAttribute, layout: CardLayoutRoles) =>
  layout.chipVisuals?.[column.attribute] ??
  CHIP_VISUAL_BY_UI_TYPE[column.uiType] ??
  ChipVisual.Raw;

export const isTrueValue = (rawValue: unknown) =>
  (typeof rawValue === 'string' || typeof rawValue === 'boolean') &&
  isStringBooleanTrue(rawValue);

const isEligibleForSlot = (
  role: SlotFallbackRole,
  rawValue: unknown,
  column: CustomViewAttribute,
) =>
  role.visual === SlotVisual.Check
    ? isTrueValue(rawValue)
    : !isEmptyAttributeValue(rawValue, column);

export interface SlotFallback {
  role: SlotFallbackRole;
  column: CustomViewAttribute;
  rawValue: unknown;
}

export const findSlotFallback = (
  { slotFallbackColumns }: CardColumns,
  layout: CardLayoutRoles,
  getRawValue: (column: CustomViewAttribute) => unknown,
): SlotFallback | undefined => {
  for (const attribute of layout.slotFallbackPriority ?? []) {
    const column = slotFallbackColumns.find((candidate) => candidate.attribute === attribute);
    const role = layout.slotFallbacks?.[attribute];
    if (!column || !role) continue;

    const rawValue = getRawValue(column);
    if (isEligibleForSlot(role, rawValue, column)) {
      return { role, column, rawValue };
    }
  }

  return undefined;
};
